import { ARREARS_RANKS, ARREARS_RANK_LABELS, type ArrearsRank } from '@ave/shared';

/**
 * One term of an instrument, asked for in the shape that term actually has.
 *
 * Shared by the record form and the classification confirm form, which ask
 * for the same terms for the same reason and used to hold two copies of the
 * labels between them. The copies had already begun to drift, which is how a
 * screen ends up calling the same term two things.
 *
 * The control follows the term rather than defaulting to a text box: a
 * ranking is a closed set and a free-text one would let somebody type
 * something the engine will refuse after they have finished filling the form
 * in, and the article that creates a ranking is prose that needs room.
 */

export const TERM_LABELS: Record<string, string> = {
  expiry_date: 'Expiry date',
  exercise_price: 'Exercise price per share',
  shares_in_issue: 'Shares in issue before exercise',
  shares_under_instrument: 'Shares under the instrument',
  issue_date: 'Issue date',
  maturity_date: 'Maturity / redemption date',
  coupon_rate: 'Coupon rate (as a decimal)',
  dividend_rate: 'Fixed dividend rate (as a decimal)',
  nominal: 'Nominal held',
  conversion_price: 'Conversion price per share',
  arrears_amount: 'Dividends in arrears',
  arrears_expected_payment_date: 'When the arrears are expected',
  arrears_ranks: 'Where the arrears rank',
  arrears_basis: 'The article that ranks them',
};

export const DATE_TERMS = new Set([
  'expiry_date',
  'issue_date',
  'maturity_date',
  'arrears_expected_payment_date',
]);

/** Terms that are prose, and need a box prose fits in. */
const TEXT_TERMS = new Set(['arrears_basis']);

export const termLabel = (key: string): string => TERM_LABELS[key] ?? key.replace(/_/g, ' ');

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const SELECT =
  'mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

export function TermField({
  termKey,
  value,
  onChange,
}: {
  termKey: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const wide = TEXT_TERMS.has(termKey);

  return (
    <label className={`text-sm text-slate-600${wide ? ' sm:col-span-2' : ''}`}>
      {termLabel(termKey)}
      {termKey === 'arrears_ranks' ? (
        <select className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose one</option>
          {ARREARS_RANKS.map((rank: ArrearsRank) => (
            <option key={rank} value={rank}>
              {ARREARS_RANK_LABELS[rank]}
            </option>
          ))}
        </select>
      ) : wide ? (
        <textarea
          className={INPUT}
          rows={2}
          value={value}
          placeholder="Article 4.3 ranks the arrears with the redemption money"
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className={INPUT}
          type={DATE_TERMS.has(termKey) ? 'date' : 'text'}
          inputMode={DATE_TERMS.has(termKey) ? undefined : 'decimal'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}
